/**
 * On-device pose extraction core (web-pose-extraction step 4).
 *
 * The testable, framework-free parts: split-cadence sampling, the BlazePose index→name map, and
 * the pose file payload builder. The payload mirrors the backend ClientPoseFile contract:
 * capture-resolution pixels, fps locked to the split cadence (frames pair with split frames BY
 * INDEX), landmark names in MediaPipe convention, conf defaulting to 1.
 */

export const SPLIT_FPS = 10;

/** Sample times matching the server's VideoSplitIntoFrames cadence: t = i/fps s from zero
 * (the fixed 10 fps unless the upload is captured at another cadence). */
export function splitSampleTimes(durationSec: number, fps: number = SPLIT_FPS): number[] {
  const times: number[] = [];
  if (!durationSec || durationSec <= 0 || !(fps > 0)) return times;
  const n = Math.ceil(durationSec * fps);
  for (let i = 0; i < n; i++) {
    const t = i / fps;
    if (t >= durationSec) break;
    times.push(Math.round(t * 1000) / 1000);
  }
  return times;
}

// BlazePose 33-landmark topology. Only the names the backend maps are consumed server-side;
// the rest are carried for future use (feet!) and skipped by the ingest.
const BLAZEPOSE_NAMES: Record<number, string> = {
  0: "nose",
  1: "left_eye_inner", 2: "left_eye", 3: "left_eye_outer",
  4: "right_eye_inner", 5: "right_eye", 6: "right_eye_outer",
  7: "left_ear", 8: "right_ear",
  9: "mouth_left", 10: "mouth_right",
  11: "left_shoulder", 12: "right_shoulder",
  13: "left_elbow", 14: "right_elbow",
  15: "left_wrist", 16: "right_wrist",
  17: "left_pinky", 18: "right_pinky",
  19: "left_index", 20: "right_index",
  21: "left_thumb", 22: "right_thumb",
  23: "left_hip", 24: "right_hip",
  25: "left_knee", 26: "right_knee",
  27: "left_ankle", 28: "right_ankle",
  29: "left_heel", 30: "right_heel",
  31: "left_foot_index", 32: "right_foot_index",
};

export function blazePoseLandmarkName(index: number): string | undefined {
  return BLAZEPOSE_NAMES[index];
}

export type ClientLandmark = {
  index: number;
  /** Normalized [0..1] coordinates as MediaPipe reports them. */
  x: number;
  y: number;
  /** MediaPipe's relative depth (unitless, smaller = nearer the camera). The server uses only its
   * ORDER between left and right to keep the sides consistent where a side view's legs cross. */
  z?: number;
  visibility?: number;
};

/** The depth field as written to the pose file: as MediaPipe gave it, to 4 decimals, only when present. */
function depthField(l: ClientLandmark): { z?: number } {
  return typeof l.z === "number" && Number.isFinite(l.z) ? { z: Math.round(l.z * 10000) / 10000 } : {};
}

export type FrameLandmarks = { landmarks: ClientLandmark[] };

/** One frame's landmarks together with the split frame they came from. */
export type KeyedFrameLandmarks = FrameLandmarks & { fileId: number };

/**
 * The pose file for labels extracted AFTER the split, from the split frame PNGs themselves. Each
 * frame names its fileId, so the server pairs by id instead of by position — which is what makes
 * the labeller's poll batch irrelevant and lets a partial result be submitted safely.
 *
 * Coordinates are in the PNG's own pixels, and captureWidth/Height are that PNG's size, so the
 * server's capture→frame scaling is the identity here and nothing about the contract changes.
 *
 * The cadence is the UPLOAD's (uploadFrames.captureFps — the splitter's stored value, 10 for
 * golf, the native rate for movement sports), never a constant: a frame-keyed pose file pairs by
 * id so it needs none, but the value it carries must agree with the frames it labels.
 */
export function buildFrameKeyedPoseFileJson(
  frames: KeyedFrameLandmarks[],
  opts: { captureWidth: number; captureHeight: number; modelVersion: string; fps: number }
): string {
  const doc = {
    modelVersion: opts.modelVersion,
    captureWidth: opts.captureWidth,
    captureHeight: opts.captureHeight,
    fps: opts.fps,
    frames: frames.map((f) => ({
      fileId: f.fileId,
      keypoints: (f.landmarks ?? [])
        .filter((l) => blazePoseLandmarkName(l.index) !== undefined)
        .map((l) => ({
          landmark_name: blazePoseLandmarkName(l.index)!,
          x: Math.round(l.x * opts.captureWidth * 100) / 100,
          y: Math.round(l.y * opts.captureHeight * 100) / 100,
          conf: l.visibility ?? 1,
          ...depthField(l),
        })),
    })),
  };
  return JSON.stringify(doc);
}

export function buildPoseFileJson(
  frames: FrameLandmarks[],
  opts: { captureWidth: number; captureHeight: number; modelVersion: string; fps?: number }
): string {
  const doc = {
    modelVersion: opts.modelVersion,
    captureWidth: opts.captureWidth,
    captureHeight: opts.captureHeight,
    fps: opts.fps ?? SPLIT_FPS,
    frames: frames.map((f) => ({
      keypoints: (f.landmarks ?? [])
        .filter((l) => blazePoseLandmarkName(l.index) !== undefined)
        .map((l) => ({
          landmark_name: blazePoseLandmarkName(l.index)!,
          // Pose file contract: CAPTURE PIXELS; the server converts to reference space.
          x: Math.round(l.x * opts.captureWidth * 100) / 100,
          y: Math.round(l.y * opts.captureHeight * 100) / 100,
          conf: l.visibility ?? 1,
          ...depthField(l),
        })),
    })),
  };
  return JSON.stringify(doc);
}
